import { $Values } from 'utility-types';
import { OfferingResponseDto, ProductDetailsResponseDto } from '@atlassian/cssxp-ccp-types';
import { FeatureFlagKeys, Permissions, Resources } from '@atlassian/cssxp-utils-generic';
import { Maybe, Scalars, TaxMetadata } from '@css-xp/graphtypes/src/generated-types-new';
import { PPCFeatureFlagKeys } from '@css-xp/ppc-constants';

export interface PartnerProfile {
  atlassianAccountId: string;
  status: string;
  displayName: string;
  email: string;
  picture: string;
  jobTitle: string | null;
  // eslint-disable-next-line no-unused-vars
  featureFlags: { [key in $Values<typeof PPCFeatureFlagKeys>]?: boolean };
  principals: string[];
  permissions: $Values<typeof Permissions>[];
  resources: $Values<typeof Resources>[];
  partnerAccounts: Array<PartnerAccount>;
  isCustomerAdvocate?: boolean;
  intercomUserHash?: string;
  staffId?: string; // Only applicable if the logged-in user is a customer advocate
}

export interface PartnerOrAdvocateProfileV3ResponseType {
  partner?: {
    partnerAccounts: Array<PartnerAccount>;
    roles: ProfileRoles;
  };
  advocate?: {
    id: string;
    status: string;
    email: string;
    displayName: string;
    picture: string;
    jobTitle: string | null;
    orgId: string;
    staffId: string;
    roles: ProfileRoles;
  };
  userPreferences: UserPreferences;
}

export interface UserPreferences {
  featureFlags: { [key in $Values<typeof FeatureFlagKeys>]?: boolean };
}

export interface ProfileRoles {
  contact: UserContact;
  billingAdmin: { [key: string]: Array<string> };
  customerAdvocate?: { [key: string]: Array<string> };
  channel?: { [key: string]: Array<string> };
}

export interface UserContact {
  id: string;
  status: string;
  name: string;
  email: string;
  picture: string;
  jobTitle: string | null;
  accountType: string;
}

export interface PartnerAccount {
  verifiedExpertise: Array<string>;
  transactionAccountId: string;
  status: string;
  specializations: Array<string>;
  role: Array<string>;
  payOnAccount: boolean;
  partnerType: string;
  parentDiscountTier: string | null;
  name: string;
  id: string;
  transactionAccountNumber?: string;
  metadata: any;
  domain: string;
  discountTier: string | undefined;
  discounts: Record<string, { label: string; percentage: number }>;
  billTo: PartnerAccountBillTo;
  alternateDomains: Array<string>;
  programLevelGlobal?: string;
  taxId?: string;
  parentId: string | null;
  parentAccounts: Array<ParentAccount>;
  relatedDomains?: Array<string>;
  internalUseLicense?: {
    startingBalance: number;
    currentBalance: number;
    currencyIsoCode: string;
    amount: number;
  };
}

export interface ParentAccount {
  alternateDomains: Array<string>;
  domain: string;
  id: string;
  parentId: string | null;
}

export type BillToTaxItems = {
  id?: Maybe<Scalars['String']>;
  label?: Maybe<Scalars['String']>;
  taxExempt?: Maybe<Scalars['Boolean']>;
  metadata?: Maybe<TaxMetadata>;
  taxIdDescription?: Maybe<Scalars['String']>;
  taxIdLabel?: Maybe<Scalars['String']>;
};

export interface PartnerAccountBillTo {
  city: string | null;
  country: string | null;
  geocodeAccuracy: any;
  latitude: any;
  longitude: any;
  postalCode: string | null;
  state: string | null;
  street: string | null;
  taxIds?: Maybe<Maybe<BillToTaxItems>[]>;
  taxId?: Maybe<string>;
}

export enum PartnerSearchContext {
  ALL = 'ALL',
  SUBSCRIPTION_ID = 'SUBSCRIPTION_ID',
  INVOICE_ID = 'INVOICE_ID',
  REFUND_ID = 'REFUND_ID',
  COMPANY = 'COMPANY',
  CUSTOMER_ADDRESS = 'CUSTOMER_ADDRESS',
  SITE_URL = 'SITE_URL',
  PERSON = 'PERSON',
  EMAIL = 'EMAIL',
  ORDER = 'ORDER',
  QUOTE = 'QUOTE',
  ORGANISATION_ID = 'ORGANISATION_ID',
  INVOICE_GROUP = 'INVOICE_GROUP',
}

export enum LayoutAppearance {
  DEFAULT = 'DEFAULT',
  ONE_COLUMN = 'ONE_COLUMN',
}

export interface OfferingResponseDtoV2 extends OfferingResponseDto {
  id: string;
  productId: string;
}

export interface ProductDetailsResponseDtoV2 extends ProductDetailsResponseDto {
  id: string;
}

export enum QuoteContractType {
  NEGOTIATED_ACA = 'NEGOTIATED_ACA',
  ELA_CONTRACT = 'ELA_CONTRACT',
  STANDARD_ACA = 'STANDARD_ACA',
}
